/**
 * WebAudio bus with lazy init + iOS unlock. All SFX calls no-op if the context
 * has not been unlocked yet — we never throw at the caller.
 */
export class AudioBus {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private unlocked = false;

  isUnlocked(): boolean {
    return this.unlocked;
  }

  getContext(): AudioContext | null {
    return this.ctx;
  }

  /** Call from the first user gesture. Safe to call multiple times. */
  unlock(): void {
    if (this.unlocked && this.ctx) return;
    try {
      if (!this.ctx) {
        const Ctor =
          (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
            .AudioContext ||
          (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.5;
        this.master.connect(this.ctx.destination);
      }
      if (this.ctx.state === 'suspended') {
        void this.ctx.resume();
      }
      // iOS unlock: play a 0-volume silent buffer in the same gesture.
      const buf = this.ctx.createBuffer(1, 1, 22050);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.ctx.destination);
      src.start(0);
      this.unlocked = true;
    } catch {
      this.unlocked = false;
    }
  }

  /** Schedule an AudioBuffer on the master bus. */
  playBuffer(buf: AudioBuffer, gain = 1): void {
    if (!this.unlocked || !this.ctx || !this.master) return;
    try {
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const g = this.ctx.createGain();
      g.gain.value = gain;
      src.connect(g);
      g.connect(this.master);
      src.start(0);
    } catch {
      // Ignore playback errors — audio is best-effort.
    }
  }
}

/** Process-wide singleton. */
export const audioBus = new AudioBus();
